import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { animationFrameScheduler, fromEvent, merge, race, Subject } from 'rxjs';
import { auditTime, filter, takeUntil, tap } from 'rxjs/operators';

import { getPos, Options, PopUpState } from './pop-up.service';

declare const Zone;

export class PopUp extends React.Component<Options, PopUpState> {
    private readonly _el: HTMLElement;
    private _done: Subject<void>;
    private readonly _container: React.RefObject<HTMLDivElement>;
    result: Promise<any>;
    private _resolve: (reason?: any) => void;
    private _reject: (reason?: any) => void;
    private _didAutoCloseBind: boolean;

    get translatePlacement(): string {
        if (!this.state.pos || !this.state.pos.placement) return null;
        const [primary, secondary] = this.state.pos.placement.split('-');
        return `${primary} ${primary}-${secondary}`;
    }

    get relativeTo(): HTMLElement {
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        return (
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            (this.props.relativeTo && this.props.relativeTo()) || document.body
        );
    }

    get isOpen(): boolean {
        return !this.state.hidden;
    }

    constructor(props: Options) {
        super(props);

        this.state = { hidden: true };

        this._el = document.createElement('div');
        this._done = new Subject();
        this._container = React.createRef();
    }

    componentDidMount(): void {
        document.body.appendChild(this._el);
        Zone.root.run(() => {
            merge(fromEvent(window, 'resize'), fromEvent(window, 'scroll'))
                .pipe(
                    takeUntil(this._done),
                    filter(() => this.isOpen),
                    auditTime(0, animationFrameScheduler),
                    tap(() => this._adjustPosition()),
                )
                .subscribe();
        });
    }

    componentDidUpdate(
        prevProps: Readonly<Options>,
        prevState: Readonly<PopUpState>,
        snapshot?: any,
    ): void {
        if (prevProps.autoClose !== this.props.autoClose) {
            if (typeof this.props.autoClose === 'string') {
                this.setState({ autoClose: this.props.autoClose.split('|') });
            } else if (this.props.autoClose === true) {
                this.setState({ autoClose: ['click', 'escape'] });
            }

            if (!this._didAutoCloseBind) {
                this._didAutoCloseBind = true;

                Zone.root.run(() => {
                    const ms = fromEvent<MouseEvent>(document, 'mouseup').pipe(
                        takeUntil(this._done),
                        filter((e) => e.button === 0),
                        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                        filter(
                            () =>
                                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                                this.state.autoClose &&
                                this.state.autoClose.some((e) => e === 'click'),
                        ),
                    );
                    const kb = fromEvent<KeyboardEvent>(
                        document,
                        'keydown',
                    ).pipe(
                        takeUntil(this._done),
                        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                        filter(
                            () =>
                                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                                this.state.autoClose &&
                                this.state.autoClose.some(
                                    (e) => e === 'escape',
                                ),
                        ),
                        filter((e) => e.which === 27),
                    );
                    race([ms, kb])
                        .pipe(tap(() => this.dismiss()))
                        .subscribe();

                    fromEvent(
                        typeof this.props.target === 'function'
                            ? this.props.target()
                            : this.props.target,
                        'mouseleave',
                    )
                        .pipe(
                            takeUntil(this._done),
                            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                            filter(
                                () =>
                                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                                    this.state.autoClose &&
                                    this.state.autoClose.some(
                                        (e) => e === 'mouseleave',
                                    ),
                            ),
                            tap((e) => {
                                e.stopPropagation();
                                this.dismiss();
                            }),
                        )
                        .subscribe();
                });
            }
        }

        this._adjustPosition();

        if (
            prevState.hidden !== this.state.hidden ||
            prevProps.backdrop !== this.props.backdrop
        ) {
            this._adjustBackdrop(!this.state.hidden && this.props.backdrop);
        }
    }

    componentWillUnmount(): void {
        this._adjustBackdrop(false);
        this._done.next();
        this._done = null;
        document.body.removeChild(this._el);
    }

    dismiss(reason?: any): void {
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._reject && this._reject(reason);
    }

    close(reason?: any): void {
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._resolve && this._resolve(reason);
    }

    // eslint-disable-next-line @typescript-eslint/promise-function-async
    show(): Promise<any> {
        this.hide();

        this.result = new Promise<any>((resolve, reject) => {
            this._resolve = resolve;
            this._reject = reject;
        });
        this.result.then(
            () => this.hide(),
            () => this.hide(),
        );

        this.setState({ hidden: false });

        return this.result;
    }

    hide(): void {
        this.dismiss();

        this.setState({ hidden: true });
    }

    private _adjustPosition(): void {
        this._el.style.position = 'absolute';
        this._el.style.width = '100%';
        this._el.style.top = '0';
        this._el.style.left = '0';
        // this._el.style.overflow = 'hidden';
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        const element = this._container && this._container.current;
        const prev = this.state.pos;
        const next =
            this.props.target &&
            getPos(
                element,
                typeof this.props.target === 'function'
                    ? this.props.target()
                    : this.props.target,
                this.props.placement,
                this.props.triangle,
            );
        if (
            next &&
            (!prev ||
                !prev.style ||
                // eslint-disable-next-line @typescript-eslint/dot-notation
                prev.style['top'] !== next.style['top'] ||
                // eslint-disable-next-line @typescript-eslint/dot-notation
                prev.style['left'] !== next.style['left'])
        ) {
            this.setState({ pos: next });
        }
        this._el.style.position = '';
        this._el.style.width = '';
        this._el.style.top = '';
        this._el.style.left = '';
        // this._el.style.overflow = '';
    }

    private _adjustBackdrop(apply: boolean): void {
        if (!apply) {
            this.relativeTo.classList.remove('pu-backdrop-parent');
        } else {
            this.relativeTo.classList.add('pu-backdrop-parent');
        }
    }

    render(): React.ReactNode {
        if (this.state.hidden) return null;
        return ReactDOM.createPortal(
            <>
                {this.props.backdrop &&
                    ReactDOM.createPortal(
                        <div
                            className={`pu-backdrop ${this.props.backdropClass}`}
                            onClick={() =>
                                this.props.backdropDismiss && this.dismiss()
                            }
                        />,
                        this.relativeTo,
                    )}
                <div
                    className={`pu-container ${this.props.containerClass}`}
                    style={this.state.pos && (this.state.pos.style as any)}
                    ref={this._container}
                >
                    {this.props.children}
                    {this.props.triangle && (
                        <div
                            className={`pu-triangle ${this.translatePlacement}`}
                        />
                    )}
                </div>
            </>,
            this._el,
        );
    }
}
